import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { AlertService } from '@core/services/alert.service';
import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import * as _ from 'lodash';
import {
  NgxFileDropEntry,
  NgxFileDropModule,
} from '@bugsplat/ngx-file-drop';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-drop-file',
  templateUrl: './drop-file.component.html',
  styleUrls: ['./drop-file.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, NgxFileDropModule],
})
export class DropFileComponent implements OnDestroy {
  @Input() textHeader!: string;
  @Input() accept!: string;
  @Input() defaultFileName = 'import_example.xlsx';
  @Input() fileInput: File | null | undefined;
  @Input() customFileName: string;
  @Input() typeExcel: string;
  @Input() disable: boolean;
  @Input() previewExampleFile = false;
  @Input() showMultiFile = false;
  @Input() showTextHint = true;
  @Output() fileOutput = new EventEmitter();

  urls: (string | ArrayBuffer | null)[] = [];
  href: string[] = [];
  files: File[] = [];
  totalFile = 0;
  subscriptions = new Subscription();

  readonly icons: Record<string, string> = {
    'application/pdf': 'file-memo.svg#file-memo',
    'application/msword': 'docx.svg#docx',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'docx.svg#docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      'xlsx.svg#xlsx',
  };

  private alert = inject(AlertService);
  private translate = inject(TranslateService);

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  deleteFile(): void {
    this.fileOutput.emit('');
  }

  dropped(event: NgxFileDropEntry[]): void {
    // Split the accept string into an array of accepted extensions
    if (this.accept) {
      const acceptedTypes = this.accept
        .split(',')
        .map((type) => type.trim().replace('.', '').toLowerCase());

      // Filter out files with invalid extensions
      const invalidFiles = event.filter((file) => {
        const fileExtension = (
          file.relativePath.split('.').pop() ?? ''
        ).toLowerCase();
        return !acceptedTypes.includes(fileExtension);
      });

      if (invalidFiles.length > 0) {
        this.alert.error(
          this.translate.instant('COMMON.Invalid File Type'),
        );
        return;
      }
    }

    for (const droppedFile of event) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry =
          droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (!this.checkFileSize(file)) {
            return;
          }
          this.onProcessFilePreview(file);
          this.files.push(file);
          this.fileOutput.emit(file);
        });
      }
    }
  }

  selectFile(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target.files || target.files.length === 0) {
      return;
    }
    _.map(target.files, (file) => {
      if (!this.checkFileSize(file)) {
        return;
      }
      if (!this.checkTotalFileSize(file)) {
        return;
      }
      this.files.push(file);
      this.onSelectFile(event);
      this.fileOutput.emit(file);
      target.value = '';
    });
  }

  onSelectFile(event: any) {
    if (event.target && event.target.files) {
      const files = event.target.files;
      if (files) {
        for (const file of files) {
          this.onProcessFilePreview(file);
        }
      }
    }
  }

  onProcessFilePreview(file: File): void {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const icon = this.icons[file.type] || 'no-image.svg#no-image';
        if (this.href && this.urls && this.showMultiFile) {
          this.href.push(`assets/images/pictograms/${icon}`);
          this.urls.push(
            file.type.startsWith('image/') ? reader.result : '',
          );
        } else {
          this.href[0] = `assets/images/pictograms/${icon}`;
          this.urls[0] = file.type.startsWith('image/')
            ? reader.result
            : '';
        }
      };
      if (reader) {
        reader.readAsDataURL(file);
      }
    }
  }

  removeFile(index: number, item: File): void {
    this.files.splice(index, 1);
    this.urls.splice(index, 1);
    this.href.splice(index, 1);
    this.totalFile -= item.size;
  }

  checkFileSize(file: File): boolean {
    if (file != null && file.size / (1000 * 1000) >= 25) {
      this.alert.error(
        this.translate.instant(
          'MEMOS.Please upload attachment files less than 25 MB',
        ),
      );
      return false;
    }
    return true;
  }

  checkTotalFileSize(file: File): boolean {
    this.totalFile += file.size;
    if (file != null && this.totalFile / (1000 * 1000) >= 45) {
      this.alert.error(
        this.translate.instant(
          'MEMOS.The total file size exceeds 45 MB',
        ),
      );
      this.totalFile -= file.size;
      return false;
    }
    return true;
  }
}
